import { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Label,
  Spinner,
  Text,
  Title2,
  Card,
  CardHeader,
  Badge,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { CheckmarkCircle24Filled, DismissCircle24Filled } from '@fluentui/react-icons';
import { saveConfig, testConnection, fetchMembers, markCredsJustStored, CWMember } from '../services/cw-api';

interface Props {
  onComplete: () => void;
}

// Official ConnectWise regional API datacenters. The picked region's url is
// stored as cw_config.baseUrl and drives the proxy's `x-cw-base` routing —
// server.js only proxies to these hosts (its allowlist). Keep the hostnames
// in sync with CW_API_HOSTS in server.js.
const CW_REGIONS = [
  { key: 'na', label: 'North America', url: 'https://api-na.myconnectwise.net/v4_6_release/apis/3.0' },
  { key: 'eu', label: 'Europe', url: 'https://api-eu.myconnectwise.net/v4_6_release/apis/3.0' },
  { key: 'au', label: 'Australia', url: 'https://api-au.myconnectwise.net/v4_6_release/apis/3.0' },
] as const;

function regionFromUrl(url?: string): string {
  if (url) {
    const match = CW_REGIONS.find(r => url.includes(`api-${r.key}.`));
    if (match) return match.key;
  }
  return 'na';
}

export default function SetupPane({ onComplete }: Props) {
  // If CW is already configured (env auto-config) but no member, skip to step 2
  const hasConfig = !!localStorage.getItem('cw_config');
  const config = hasConfig ? JSON.parse(localStorage.getItem('cw_config')!) : null;
  const needsMemberOnly = hasConfig && config && (!config.memberId || config.memberId === 0);
  const [step, setStep] = useState(needsMemberOnly ? 2 : 1);
  const [companyId, setCompanyId] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [region, setRegion] = useState<string>(() => regionFromUrl(config?.baseUrl));
  const baseUrl = (CW_REGIONS.find(r => r.key === region) ?? CW_REGIONS[0]).url;
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [members, setMembers] = useState<CWMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [loadingMembers, setLoadingMembers] = useState(false);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    const authString = `${companyId}+${publicKey}:${privateKey}`;
    const auth = btoa(authString);
    // CW's `clientId` header is the DEVELOPER UUID (OEL's registered ID with
    // ConnectWise), NOT the company identifier. Every OEL user shares the same
    // UUID; their per-user creds go in companyId/publicKey/privateKey.
    const CW_DEVELOPER_CLIENT_ID = 'd9d94262-8745-45a3-86ad-41271fce34c1';
    saveConfig({ baseUrl, auth, clientId: CW_DEVELOPER_CLIENT_ID, memberId: 0 });
    // Stamp when these creds were stored so LicenseBanner can warn the
    // user to rotate at 180+ days (CREDS_AGE_WARN_DAYS in cw-api.ts).
    markCredsJustStored();

    try {
      await testConnection();
      setTestResult({ ok: true, msg: 'Connected!' });
    } catch (err) {
      const msg = String(err);
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setTestResult({ ok: false, msg: 'Could not connect to ConnectWise API. Check your credentials and URL.' });
      } else if (msg.includes('401') || msg.includes('403')) {
        setTestResult({ ok: false, msg: 'Authentication failed. Check your Company ID, Public Key, Private Key, and that the selected ConnectWise Region matches your tenant.' });
      } else {
        setTestResult({ ok: false, msg: `Connection error: ${msg.replace(/^Error:\s*/, '')}` });
      }
    }
    setTesting(false);
  };

  const handleLoadMembers = async () => {
    setLoadingMembers(true);
    try {
      const m = await fetchMembers();
      setMembers(m);
    } catch (err) {
      setTestResult({ ok: false, msg: `Failed to load members: ${err}` });
    }
    setLoadingMembers(false);
  };

  // Auto-load members if env-configured (skip to step 2)
  useEffect(() => {
    if (needsMemberOnly) {
      handleLoadMembers();
    }
  }, [needsMemberOnly]);

  const handleFinish = () => {
    const existing = JSON.parse(localStorage.getItem('cw_config') || '{}');
    existing.memberId = Number(selectedMemberId);
    localStorage.setItem('cw_config', JSON.stringify(existing));
    onComplete();
  };

  return (
    <div style={{ padding: 16, maxWidth: 400 }}>
      <Title2>CW Email Logger Setup</Title2>

      {step === 1 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Text>Enter your ConnectWise API credentials.</Text>

          <div>
            <Label htmlFor="companyId">Company ID</Label>
            <Input id="companyId" value={companyId} onChange={(_, d) => setCompanyId(d.value)} placeholder="e.g., bluwave" style={{ width: '100%' }} />
          </div>

          <div>
            <Label htmlFor="publicKey">Public Key</Label>
            <Input id="publicKey" value={publicKey} onChange={(_, d) => setPublicKey(d.value)} placeholder="API public key" style={{ width: '100%' }} />
          </div>

          <div>
            <Label htmlFor="privateKey">Private Key</Label>
            <Input id="privateKey" type="password" value={privateKey} onChange={(_, d) => setPrivateKey(d.value)} placeholder="API private key" style={{ width: '100%' }} />
          </div>

          <div>
            <Label htmlFor="region">ConnectWise Region</Label>
            <RadioGroup id="region" value={region} onChange={(_, d) => setRegion(d.value)}>
              {CW_REGIONS.map(r => (
                <Radio key={r.key} value={r.key} label={r.label} />
              ))}
            </RadioGroup>
          </div>

          {testResult && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {testResult.ok ? <CheckmarkCircle24Filled color="green" /> : <DismissCircle24Filled color="red" />}
              <Text style={{ color: testResult.ok ? 'green' : 'red' }}>{testResult.msg}</Text>
            </div>
          )}

          <Button appearance="primary" onClick={handleTest} disabled={!companyId || !publicKey || !privateKey || testing}>
            {testing ? <Spinner size="tiny" /> : 'Test Connection'}
          </Button>

          {testResult?.ok && (
            <Button appearance="primary" onClick={() => { setStep(2); handleLoadMembers(); }}>
              Next
            </Button>
          )}
        </div>
      )}

      {step === 2 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Text>Select your CW member profile:</Text>

          {testResult && !testResult.ok && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DismissCircle24Filled color="red" />
              <Text style={{ color: 'red' }}>{testResult.msg}</Text>
            </div>
          )}

          {loadingMembers ? (
            <Spinner label="Loading members..." />
          ) : members.length === 0 ? (
            <div>
              <Text>Could not load members. </Text>
              <Button onClick={handleLoadMembers}>Retry</Button>
            </div>
          ) : (
            <RadioGroup value={selectedMemberId} onChange={(_, d) => setSelectedMemberId(d.value)}>
              {members
                .map(m => ({
                  id: m.id,
                  name: `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.identifier || `Member ${m.id}`,
                }))
                .filter(m => m.name.length > 0)
                .map(m => (
                  <Radio key={m.id} value={String(m.id)} label={m.name} />
                ))}
            </RadioGroup>
          )}

          {selectedMemberId && (
            <Button appearance="primary" onClick={handleFinish}>
              Start Using CW Email Logger
            </Button>
          )}

          <Button onClick={() => setStep(1)}>Back</Button>
        </div>
      )}
    </div>
  );
}
